import { ContinueButton } from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import { FontWeight } from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import { gsap } from "gsap";
import { Graphics, Text } from "pixi.js";

import { EventsManager } from "../../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../../abstracts/flow/AbstractGameMode";
import { AbstractBitmapText } from "../../../../abstracts/graphics/AbstractBitmapText";
import { AbstractSpine } from "../../../../abstracts/graphics/AbstractSpine";
import { AbstractView } from "../../../../abstracts/modules/AbstractView";
import { AbstractWinScreen } from "../../../../abstracts/modules/screens/AbstractWinScreen";
import { LevelName, TimelineLabels, WinType } from "../../../../abstracts/types/enums";
import { ITotalWinScreenData } from "../../../../abstracts/types/interfaces";
import { fitDisplayObjectToWidth } from "../../../../abstracts/utils/GraphicUtils";
import {
    fadeAndStopSound,
    fadeInBackgroundMusic,
    fadeOutBackgroundMusic,
    fadeSound,
    playNextBackgroundMusic,
    playSound,
    startAmbSounds,
    stopBackgroundMusic,
    stopSound,
} from "../../../../abstracts/utils/SoundUtils";
import { Apocalypse } from "../../../Apocalypse";
import {
    ApocalypseFontsNames,
    ApocalypseSoundNames,
} from "../../../assetsLoader/ApocalypseAssetsNames";
import { winFontSize, winLevels } from "../../../config/ApocalypseConfig";
import { RESTORE_BG_BILLBOARDS } from "../../../events/ApocalypseEvents";
import { fadeInRadioSound, fadeOutRadioSound } from "../../Sound/ApocalypseSoundController";
import { getBigWinTextAnimationConfig } from "../ApocalypseWinTextPositionConfig";

export class ApocalypseTotalWinScreen extends AbstractWinScreen {
    protected currentValue: number = 0;
    protected currentSpine: AbstractSpine;
    protected waysWinText: Text;
    protected spacingBetweenWinAndWays: number;
    protected totalWinTextDefaultYPosition: number = -10;
    protected bigWinsBg: Graphics;

    // private particlesEmitter: Emitter;
    // private particleContainer: ParticleContainer;

    constructor(parentContainer: AbstractView) {
        super(`ApocalypseTotalWinScreen`, parentContainer);
    }

    protected init(): void {
        super.init();

        this.totalWinText = new AbstractBitmapText({
            text: `0`,
            style: {
                fontName: ApocalypseFontsNames.APOCALYPSE_BLUE,
                fontSize: winFontSize,
            },
        });
        this.totalWinText.name = `TotalWinText`;
        this.totalWinText.y = getBigWinTextAnimationConfig(1).initialY;

        this.waysWinText = new Text(`WAYS`, {
            dropShadow: true,
            dropShadowAngle: 8,
            dropShadowBlur: 4,
            dropShadowDistance: 2,
            fontFamily: `Helvetica`,
            fontWeight: FontWeight.BOLD,
            fontSize: 42,
            align: `center`,
            wordWrap: true,
            wordWrapWidth: 560,
            fill: [`#f7c238`, `#c77826`],
        });
        this.waysWinText.name = `waysWinText`;
        this.waysWinText.y = 48;
        // this.addChild(this.waysWinText);

        this.spacingBetweenWinAndWays = 58;

        this.activeSpines = [];
        for (let i = 0; i < 6; i++) {
            const bigWinSpine = new AbstractSpine(`spine/Apocalypse/lvl${i + 1}.json`);
            bigWinSpine.name = i === 5 ? `MaxCapSpine` : `BigWinSpine${i + 1}`;
            bigWinSpine.scale.set(0.45);
            bigWinSpine.y = 48;
            this.activeSpines.push(bigWinSpine);
        }

        this.bigWinsBg = new Graphics();
        this.bigWinsBg.beginFill(0x000000, 0.8);
        this.bigWinsBg.drawRect(-1, -1, 2, 2);
        this.bigWinsBg.endFill();

        this.bigWinsBg.width = 1680;
        this.bigWinsBg.height = 1680;

        // this.particleContainer = new ParticleContainer();
        //
        // this.particleContainer.x = -80;
        // this.particleContainer.y = 80;

        /*this.particlesEmitter = new Emitter(this.particleContainer, [`can1`, `can2`, `can3`], {
            alpha: {
                start: 1,
                end: 0,
            },
            scale: {
                start: 0.5,
                end: 1,
                minimumScaleMultiplier: 0.75,
            },
            color: {
                start: `#ffffff`,
                end: `#ffffff`,
            },
            speed: {
                start: 200,
                end: 400,
                minimumSpeedMultiplier: 3,
            },
            acceleration: {
                x: 0,
                y: 400,
            },
            maxSpeed: 0,
            startRotation: {
                min: 240,
                max: 300,
            },
            noRotation: false,
            rotationSpeed: {
                min: 10,
                max: 20,
            },
            lifetime: {
                min: 5.8,
                max: 6,
            },
            blendMode: `normal`,
            frequency: 0.1,
            emitterLifetime: 5.8,
            maxParticles: 120,
            pos: {
                x: 0,
                y: 0,
            },
            addAtBack: true,
            spawnType: `rect`,
            spawnRect: {
                x: -230,
                y: 0,
                w: 400,
                h: 0,
            },
            autoUpdate: true,
        });*/
    }

    protected startLowerWinCounterAnimation(
        winAmount: number,
        animationDuration: number,
        winType: WinType,
        timeline: gsap.core.Timeline,
        waysWon: number = -1,
        onStart: Function | null = null,
        onUpdate: Function | null = null,
        onComplete: Function | null = null,
        callback: Function | null = null
    ) {
        this.currentValue = winAmount;

        this.totalWinText.cacheAsBitmap = false;

        const isLow = winType === WinType.TINY || winType === WinType.SMALL;
        const winObj: { value: number } = {
            value: isLow ? winAmount : 0,
        };

        const onCounterStart = () => {
            this.selfAddToParent();
            this.totalWinText.scale.set(0.5);

            if (waysWon <= 0) {
                this.totalWinText.y = 14;
                this.removeChild(this.waysWinText);
            } else {
                this.totalWinText.y = this.totalWinTextDefaultYPosition;

                const waysWonString =
                    waysWon === 1
                        ? Apocalypse.apiPlugin.translations.translate(`Way Won`)
                        : Apocalypse.apiPlugin.translations.translate(`Ways Won`);

                this.waysWinText.text = `${waysWon} ${waysWonString}`.toUpperCase();

                this.waysWinText.x = -this.waysWinText.width / 2;
                this.waysWinText.y = this.totalWinText.y + this.spacingBetweenWinAndWays;
                this.addChild(this.waysWinText);
            }

            onStart !== null && onStart();

            callback !== null && callback();
        };
        const onCounterUpdate = () => {
            this.totalWinText.updateValue(winObj.value.toFixed(2).toString());

            onUpdate !== null && onUpdate();

            this.checkWinTextWidth();
        };

        const onCounterComplete = () => {
            onComplete !== null && onComplete();
        };

        const counterAnimation = gsap.to(winObj, {
            value: winAmount,
            duration: animationDuration,
            ease: `none`,
            onStart: onCounterStart,
            onUpdate: onCounterUpdate,
            onComplete: onCounterComplete,
        });

        timeline.add(counterAnimation, TimelineLabels.FINISH_SYMBOLS_WIN_ANIMATION);

        timeline.addLabel(TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT);

        const highlightAnimation = this.getHighlightWinTextAnimation(winType);
        timeline.add(highlightAnimation, TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT);

        timeline.addLabel(
            TimelineLabels.TEXT_COUNT_COMPLETE,
            TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT
        );

        const sharpenFont = () => {
            this.totalWinText.cacheAsBitmap = true;
            // @ts-ignore
            this.totalWinText.filters = null;
        };
        timeline.call(sharpenFont, undefined, `>`);
    }

    protected getHighlightWinTextAnimation(winType: WinType): gsap.core.Tween {
        return gsap.to(this.totalWinText, {
            duration: 0.1,
            pixi: {
                colorize: `white`,
                colorizeAmount: 1,
                brightness: 2,
                scale: `+=0.1`,
            },
            yoyo: true,
            repeat: 1,
            repeatDelay: 0.1,
            onStart: () => {
                if (winType === WinType.TINY || winType === WinType.SMALL) {
                    playSound({ name: ApocalypseSoundNames.WIN_FALLBACK_SMALL });
                } else if (winType === WinType.MEDIUM || winType === WinType.LARGE) {
                    playSound({ name: ApocalypseSoundNames.WIN_FALLBACK_LARGE });
                }
            },
            onUpdate: () => {
                fitDisplayObjectToWidth(this.totalWinText, 720);
            },
        });
    }

    protected onLowerWinsStart(): void {
        this.addChild(this.totalWinText);
    }

    public override handleTinyWins(data: ITotalWinScreenData): void {
        super.handleTinyWins(data);

        const onStart = () => {
            this.onLowerWinsStart();

            playSound({ name: ApocalypseSoundNames.WIN_TINY });
        };

        const onUpdate = () => {};

        this.startLowerWinCounterAnimation(
            data.winAmount!,
            data.animationDuration!,
            data.winType!,
            data.timeline,
            data.waysWon!,
            onStart,
            onUpdate,
            null,
            data.callback
        );
    }

    public override handleSmallWins(data: ITotalWinScreenData): void {
        super.handleSmallWins(data);

        const onStart = () => {
            this.onLowerWinsStart();

            playSound({ name: ApocalypseSoundNames.WIN_SMALL });
        };

        const onUpdate = () => {};

        this.startLowerWinCounterAnimation(
            data.winAmount!,
            data.animationDuration!,
            data.winType!,
            data.timeline!,
            data.waysWon!,
            onStart,
            onUpdate,
            null,
            data.callback
        );
    }

    public override handleMediumWins(data: ITotalWinScreenData) {
        super.handleMediumWins(data);

        const onStart = () => {
            this.onLowerWinsStart();

            playSound({ name: ApocalypseSoundNames.WIN_COUNTUP });
            gsap.delayedCall(data.animationDuration!, () => {
                fadeSound({
                    name: ApocalypseSoundNames.WIN_COUNTUP,
                    from: 1,
                    to: 0,
                    duration: 0.5,
                });
            });
        };

        const onComplete = () => {
            playSound({ name: ApocalypseSoundNames.WIN_MEDIUM });
        };

        const onUpdate = () => {};

        this.startLowerWinCounterAnimation(
            data.winAmount!,
            data.animationDuration!,
            data.winType!,
            data.timeline,
            data.waysWon!,
            onStart,
            onUpdate,
            onComplete,
            data.callback
        );
    }

    public override handleLargeWins(data: ITotalWinScreenData) {
        super.handleLargeWins(data);

        const onStart = () => {
            this.onLowerWinsStart();

            playSound({ name: ApocalypseSoundNames.WIN_COUNTUP });
            gsap.delayedCall(data.animationDuration!, () => {
                fadeSound({
                    name: ApocalypseSoundNames.WIN_COUNTUP,
                    from: 1,
                    to: 0,
                    duration: 0.5,
                });
            });
        };

        const onComplete = () => {
            playSound({ name: ApocalypseSoundNames.WIN_LARGE });
        };

        const onUpdate = () => {};

        this.startLowerWinCounterAnimation(
            data.winAmount!,
            data.animationDuration!,
            data.winType!,
            data.timeline!,
            data.waysWon!,
            onStart,
            onUpdate,
            onComplete,
            data.callback
        );
    }

    public override handleBigWins(data: ITotalWinScreenData): void {
        super.handleBigWins(data);

        const levelNumber: number = +data.level!.name.replace(/^\D+/g, ``);

        const highlightAnimation =
            data.level === data.finalLevel
                ? this.getHighlightWinTextAnimation(data.winType!)
                : gsap.to({}, { duration: 0 });

        let initialValue =
            data.level?.name === LevelName.BIG_WIN_1 ? 0 : data.betAmount! * data.level?.min!;

        let finalValue =
            data.winAmount! < data.betAmount! * data.level?.max!
                ? data.winAmount!
                : data.betAmount! * data.level?.max!;

        const winObj: { value: number } = {
            value: initialValue,
        };

        if (
            !(
                data.level?.name !== LevelName.BIG_WIN_1 &&
                finalValue <= Math.round(data.level!.min * data.betAmount! * 1.1)
            )
        ) {
            const nextLevelIndex = winLevels.indexOf(data.level!) + 1;

            if (nextLevelIndex < winLevels.length) {
                const nextLevel = winLevels[nextLevelIndex];
                if (nextLevel.winType !== WinType.MAX_CAP) {
                    if (
                        nextLevel === data.finalLevel! &&
                        data.winAmount! <= Math.round(nextLevel.min * data.betAmount! * 1.1)
                    ) {
                        finalValue = data.winAmount!;
                    }
                } else if (
                    nextLevel === data.finalLevel! &&
                    nextLevel.winType === WinType.MAX_CAP
                ) {
                    finalValue = 18800 * data.betAmount!;
                }
            }

            this.currentValue = finalValue;

            const positionConfig = getBigWinTextAnimationConfig(levelNumber);

            const counterAnimation = gsap.to(winObj, {
                value: finalValue,
                duration: data.level?.animationDuration!,
                ease: `none`,
                onStart: () => {
                    this.selfAddToParent();

                    this.addChild(this.activeSpines[levelNumber - 1]);

                    this.currentSpine = this.activeSpines[levelNumber - 1];

                    const tempSpine = this.activeSpines[levelNumber - 1];
                    // ! delete if when all spines animations are in place
                    tempSpine.alpha = 0;
                    gsap.to(tempSpine, {
                        alpha: 1,
                        duration: 0.02,
                        delay: 0.03,
                    });
                    tempSpine.getSpineTween(`animation`, false, () => {
                        if (levelNumber === 1) {
                            this.totalWinText.alpha = 0;
                            gsap.to(this.totalWinText, {
                                alpha: 1,
                                duration: 0.3,
                            });

                            this.bigWinsBg.alpha = 0;
                            this.addChildAt(this.bigWinsBg, 0);
                            gsap.to(this.bigWinsBg, {
                                alpha: 1,
                                duration: 0.3,
                            });
                        }
                        this.addChild(this.totalWinText);

                        if (levelNumber > 1) {
                            gsap.delayedCall(0.3, () => {
                                this.removeChild(this.activeSpines[levelNumber - 2]);
                                this.activeSpines[levelNumber - 2].reset();
                            });

                            // if (data.level!.name === LevelName.BIG_WIN_1) {
                            // EventsManager.trigger(GameEvents.HIDE_XBET_BUTTON);
                            stopSound({ name: ApocalypseSoundNames.BIG_WIN_INTRO });
                            // }
                        }
                        // else {
                        //     playSound({ name: ApocalypseSoundNames.BIG_WIN_INTRO });
                        // }
                    });

                    this.totalWinText.y = positionConfig.initialY;

                    if (this.currentSoundName !== ``) {
                        fadeAndStopSound({
                            name: this.currentSoundName,
                            from: 1,
                            to: 0,
                            duration: 0.01,
                        });
                    }

                    this.currentSoundName = `bigwin${levelNumber}`;
                    // Reynir
                    gsap.delayedCall(0, () => {
                        playSound({ name: this.currentSoundName });
                    });

                    const callback = data.callback!;
                    callback !== null && callback();
                },
                onUpdate: () => {
                    this.totalWinText.updateValue(winObj.value.toFixed(2).toString());
                    this.checkWinTextWidth();
                },
            });

            this.currentSpine = this.activeSpines[levelNumber - 1];

            data.timeline.add(counterAnimation, data.level?.name!);

            const bounceTextOnLevelChangeAnimation = gsap.fromTo(
                this.totalWinText,
                {
                    pixi: { scale: 0.4 },
                },
                {
                    pixi: { scale: 0.5 },
                    duration: 0.5,
                    ease: `back.out(2.4)`,
                    onUpdate: () => {
                        this.checkWinTextWidth();
                    },
                }
            );

            data.timeline.add(bounceTextOnLevelChangeAnimation, data.level?.name!);
        }

        if (data.level === data.finalLevel) {
            data.timeline.addLabel(TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT);

            data.timeline.add(highlightAnimation, TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT);

            data.timeline.call(
                () => {
                    playSound({ name: ApocalypseSoundNames.BIG_WIN_END });
                },
                undefined,
                TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT
            );

            data.timeline.addLabel(TimelineLabels.TEXT_COUNT_COMPLETE);
        }

        if (data.level?.name === LevelName.BIG_WIN_1) {
            // playSound({ name: ApocalypseSoundNames.BIG_WIN_INTRO });
            fadeOutBackgroundMusic(1.6);
            fadeOutRadioSound(3);
        }
    }

    public onBigWinsComplete(data: ITotalWinScreenData) {
        super.onBigWinsComplete(data);

        const bigWinsEndTimeline = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
            onStart: () => {
                fadeSound({
                    name: this.currentSoundName,
                    from: 1,
                    to: 0,
                    duration: 0.01,
                });

                if (Apocalypse.model.data.mode === AbstractGameMode.NORMAL) {
                    fadeInBackgroundMusic(2);
                    if (!Apocalypse.model.gameData.brokeTheBank) {
                        fadeInRadioSound();
                    }
                }
            },
            onComplete: () => {
                const callback = data.callback!;
                callback !== null && callback();
            },
        });

        const moveTotalWinToCenterAnimation = gsap.to(this.totalWinText, {
            y: this.totalWinTextDefaultYPosition,
            pixi: { scale: 0.5 },
            duration: 0.4,
            ease: `back.out(2)`,
            onStart: () => {
                const callback = data.callback!;
                callback !== null && callback();

                const waysWonString =
                    data.waysWon === 1
                        ? Apocalypse.apiPlugin.translations.translate(`Way Won`)
                        : Apocalypse.apiPlugin.translations.translate(`Ways Won`);

                this.waysWinText.text = `${data.waysWon} ${waysWonString}`.toUpperCase();
                this.waysWinText.x = -this.waysWinText.width / 2;
                this.addChild(this.waysWinText);
            },
            onUpdate: () => {
                this.checkWinTextWidth();

                this.waysWinText.y = this.totalWinText.y + this.spacingBetweenWinAndWays;
            },
            onComplete: () => {
                const callback = data.callback!;
                callback !== null && callback();
            },
        });

        const showWaysTextAnimation = gsap.fromTo(
            this.waysWinText,
            { alpha: 0 },
            {
                alpha: 1,
                duration: moveTotalWinToCenterAnimation.totalDuration() / 2,
            }
        );

        const hideCurrentSpineAnimation = gsap.to(this.currentSpine, {
            duration: moveTotalWinToCenterAnimation.totalDuration(),
            alpha: 0,
        });

        if (Apocalypse.model.data.mode !== AbstractGameMode.NORMAL) {
            const hideTotalWinTextAnimation = gsap.to(this.totalWinText, {
                duration: 0.04,
                alpha: 0,
            });

            bigWinsEndTimeline.add(hideTotalWinTextAnimation, 0);
        }

        bigWinsEndTimeline.add(hideCurrentSpineAnimation, 0);
        bigWinsEndTimeline.add(moveTotalWinToCenterAnimation, 0);
        bigWinsEndTimeline.add(
            showWaysTextAnimation,
            moveTotalWinToCenterAnimation.totalDuration() / 2
        );

        data.timeline.add(bigWinsEndTimeline, `${TimelineLabels.ON_BIG_WINS_COMPLETE_START}+=0`);

        data.timeline.addLabel(TimelineLabels.ON_BIG_WINS_COMPLETE_FINISH, `>+=1.5`);
    }

    protected startParticlesEmitter() {
        // this.particlesEmitter.emit = true;
        // this.addChild(this.particleContainer);
    }

    protected stopParticlesEmitter() {
        // this.particlesEmitter.emit = false;
    }

    public override handleMaxCap(data: ITotalWinScreenData): void {
        super.handleMaxCap(data);

        const winObj: { value: number } = {
            value: 18800 * data.betAmount!,
        };

        const highlightAnimation = this.getHighlightWinTextAnimation(data.winType!);

        const counterAnimation = gsap.to(winObj, {
            value: data.winAmount,
            duration: data.level?.animationDuration! - highlightAnimation.totalDuration(),
            ease: `none`,
            onStart: () => {
                this.addChild(this.activeSpines[5]);
                this.removeChild(this.currentSpine);
                this.currentSpine = this.activeSpines[5];

                this.currentSpine.getSpineTween(`animation`);

                this.totalWinText.y = 188;

                this.addChild(this.totalWinText);

                const callback = data.callback!;
                callback !== null && callback();

                fadeSound({
                    name: `bigwin5`,
                    from: 1,
                    to: 0,
                    duration: 0.01,
                });

                this.removeChild(this.waysWinText);

                playSound({ name: ApocalypseSoundNames.MAX_CAP_COUNTUP });

                // this.startParticlesEmitter();
            },
            onUpdate: () => {
                this.totalWinText.updateValue(winObj.value.toFixed(2).toString());
                this.checkWinTextWidth();
            },
        });

        data.timeline.add(counterAnimation, data.level?.name!);

        const scaleUpTextAnimation = gsap.to(this.totalWinText, {
            pixi: { scale: 0.5 },
            ease: `none`,
            duration: counterAnimation.duration(),
            onUpdate: () => {
                this.checkWinTextWidth();
            },
        });

        data.timeline.add(scaleUpTextAnimation, data.level?.name!);

        data.timeline.addLabel(TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT);
        data.timeline.add(highlightAnimation, TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT);

        const moveTextAboveButtonAnimation = gsap.to(this.totalWinText, {
            y: 180,
            duration: highlightAnimation.totalDuration(),
        });

        data.timeline.add(moveTextAboveButtonAnimation, TimelineLabels.HIGHLIGHT_TOTAL_WIN_TEXT);

        const sharpenFont = () => {
            this.totalWinText.cacheAsBitmap = true;
            // @ts-ignore
            this.totalWinText.filters = null;
        };
        data.timeline.call(sharpenFont, undefined, `>`);

        data.timeline.addLabel(TimelineLabels.SHOW_CONTINUE_BUTTON);

        data.timeline.call(
            () => {
                playSound({ name: ApocalypseSoundNames.MAX_CAP_LOOP, volume: 0, repeat: 10000 });
                fadeSound({
                    name: ApocalypseSoundNames.MAX_CAP_LOOP,
                    from: 0,
                    to: 1,
                    duration: 0.25,
                });

                this.currentSpine.getSpineTween(`post_count`, true);

                this.continueButton = new ContinueButton();
                this.addChild(this.continueButton);

                this.continueButton.enable(true);
                this.continueButton.x = -this.continueButton.width / 2;
                this.continueButton.y = 286;

                const continueBtnCallback = () => {
                    this.removeChild(this.continueButton);
                    this.continueButton.destroy();

                    data.timeline.resume();

                    fadeAndStopSound({
                        name: ApocalypseSoundNames.MAX_CAP_LOOP,
                        from: 1,
                        to: 0,
                        duration: 0.5,
                    });

                    stopBackgroundMusic();
                    playNextBackgroundMusic();
                    startAmbSounds();
                    playSound({ name: ApocalypseSoundNames.CONTINUE_BUTTON });
                    // playSound({ name: ApocalypseSoundNames.BIG_WIN_END });
                };

                this.continueButton.addClickCallback(continueBtnCallback);

                this.addChild(this.continueButton);

                this.stopParticlesEmitter();

                data.timeline.pause();
            },
            undefined,
            TimelineLabels.SHOW_CONTINUE_BUTTON
        );

        data.timeline.addLabel(
            TimelineLabels.TEXT_COUNT_COMPLETE,
            `${TimelineLabels.SHOW_CONTINUE_BUTTON}-=0.02`
        );
    }

    public onMaxCapComplete(data: ITotalWinScreenData) {
        super.onMaxCapComplete(data);

        data.timeline.addLabel(
            TimelineLabels.ON_MAX_CAP_COMPLETE_START,
            TimelineLabels.SHOW_CONTINUE_BUTTON
        );

        data.timeline.addLabel(
            TimelineLabels.SHOW_NEXT_BACKGROUND,
            TimelineLabels.SHOW_CONTINUE_BUTTON
        );

        const showFinalTextTimeline = gsap.timeline({});

        const moveTotalWinToCenterAnimation = gsap.to(this.totalWinText, {
            // x: 0,
            y: 0, //this.totalWinTextDefaultYPosition,
            pixi: { scale: 0.5 },
            duration: 0.5,
            ease: `back.out(2)`,
            onUpdate: () => {
                this.checkWinTextWidth();
            },
            onComplete: () => {
                const callback = data.callback!;
                callback !== null && callback();

                this.currentSoundName = ``;
            },
        });

        const alphaObj: { value: number } = { value: 1 };

        const hideMaxCapSpineAnimation = gsap.to(alphaObj, {
            duration: moveTotalWinToCenterAnimation.totalDuration(),
            value: 0,
            onUpdate: () => {
                this.activeSpines[5].alpha = alphaObj.value;
            },
            onComplete: () => {
                this.activeSpines.forEach((spine) => {
                    this.removeChild(spine);
                    spine.reset();
                });
            },
        });

        showFinalTextTimeline.add(moveTotalWinToCenterAnimation, 0);
        showFinalTextTimeline.add(hideMaxCapSpineAnimation, 0);

        data.timeline.add(showFinalTextTimeline, TimelineLabels.ON_MAX_CAP_COMPLETE_START);
        data.timeline.addLabel(
            TimelineLabels.ON_MAX_CAP_COMPLETE_FINISH,
            `${TimelineLabels.ON_MAX_CAP_COMPLETE_START}+=${showFinalTextTimeline.duration() + 1.5}`
        );
    }

    public finish(data: ITotalWinScreenData) {
        gsap.to(this.totalWinText, {
            duration: data.finishTextAnimationDuration,
            alpha: 0,
            // x: 0,
            y: -220,
            onStart: () => {
                if (data.waysWon! <= 0 || data.winType === WinType.MAX_CAP) {
                    this.removeChild(this.waysWinText);
                }
                EventsManager.trigger(RESTORE_BG_BILLBOARDS);

                gsap.to(this.bigWinsBg, {
                    alpha: 0,
                    duration: 0.3,
                    onComplete: () => {
                        this.removeChild(this.bigWinsBg);
                    },
                });
            },
            onUpdate: () => {
                if (data.waysWon! > 0) {
                    this.waysWinText.y = this.totalWinText.y + this.spacingBetweenWinAndWays;
                    this.waysWinText.alpha = this.totalWinText.alpha;
                }
            },
            onComplete: () => {
                this.removeChild(this.totalWinText);
                this.totalWinText.alpha = 1;

                this.totalWinText.position.set(0, 300);

                this.removeChild(this.waysWinText);
                this.waysWinText.alpha = 1;

                // @ts-ignore
                this.totalWinText.filters = null;
                this.selfRemoveFromParent();

                this.activeSpines.forEach((spine) => {
                    this.removeChild(spine);
                    spine.reset();
                });

                this.currentSoundName = ``;
            },
        });

        this.currentValue = 0;
    }

    public override showWaysWin(
        waysWon: number,
        duration: number = 0.5
    ): gsap.core.Timeline | gsap.core.Tween {
        const tl = gsap.timeline({});

        const showWinText = gsap.fromTo(
            this.waysWinText,
            { alpha: 0 },
            {
                alpha: 1,
                duration: 0.2 * duration,
                onStart: () => {
                    const waysWonString =
                        waysWon === 1
                            ? Apocalypse.apiPlugin.translations.translate(`Way Won`)
                            : Apocalypse.apiPlugin.translations.translate(`Ways Won`);

                    this.waysWinText.text = `${waysWon} ${waysWonString}`.toUpperCase();
                    this.waysWinText.x = -this.waysWinText.width / 2;
                    this.waysWinText.y = this.totalWinText.y + this.spacingBetweenWinAndWays;
                    this.addChild(this.waysWinText);

                    this.removeChild(this.totalWinText);
                    this.selfAddToParent();
                },
            }
        );
        tl.add(showWinText, 0);

        const hideWinText = gsap.fromTo(
            this.waysWinText,
            { alpha: 1 },
            {
                alpha: 0,
                duration: 0.1 * duration,
                ease: `none`,
                onComplete: () => {
                    this.removeChild(this.waysWinText);
                    this.addChild(this.totalWinText);
                    this.selfRemoveFromParent();
                },
            }
        );
        tl.add(hideWinText, 0.9 * duration);

        return tl;
    }

    protected checkWinTextWidth(): void {
        fitDisplayObjectToWidth(this.totalWinText, 700);
    }
}
